import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// ─── ALL FAQ DATA ─────────────────────────────────────────────────────────────
const FAQ_SECTIONS = [
  {
    id: "general",
    icon: "◎",
    title: "General",
    color: "#F05A41",
    questions: [
      {
        q: "What is miniDevToolkit?",
        a: "miniDevToolkit is a free collection of 25+ browser-based developer tools available at minidevtoolkit.com. It includes a JSON toolkit, encoding and decoding tools, hash generators, UUID and QR code generators, fake data generator, file converter, image compressor, time and date tools, and text utilities. Everything runs entirely in your browser — no data is ever sent to a server and no account is required.",
      },
      {
        q: "Is miniDevToolkit completely free?",
        a: "Yes. Every tool on miniDevToolkit is 100% free with no subscription, no sign-up, no account, and no hidden fees. The tools are free to use forever.",
      },
      {
        q: "Is my data safe when using miniDevToolkit?",
        a: "Yes. All tools run entirely client-side in your browser using JavaScript. Your JSON, files, passwords, text, and images are never sent to any server. Everything is processed locally on your device.",
      },
      {
        q: "Do I need to create an account?",
        a: "No account or sign-up is required. Open the site and start using any tool immediately. Your session history and theme preference are saved in your browser's local storage so they persist between visits.",
      },
      {
        q: "Does miniDevToolkit work on mobile?",
        a: "Yes. miniDevToolkit is fully mobile responsive. The sidebar collapses into a drawer on small screens and all tools are usable on mobile and tablet devices.",
      },
      {
        q: "Does miniDevToolkit support dark mode?",
        a: "Yes. Click the theme toggle button in the top right corner to switch between dark and light mode. Your preference is saved and remembered across sessions.",
      },
      {
        q: "How many tools does miniDevToolkit have?",
        a: "miniDevToolkit currently has 25+ individual tools across 9 toolkits: JSON Toolkit (7 tools), Encoding Toolkit (14 tools), Generators (4 tools), Fake Data Generator, Hash & Security (4 tools), File Converter (4 converters), Time & Date (3 tools), Text Utilities (4 tools), and Compression Toolkit (5 tools).",
      },
      {
        q: "Can I use miniDevToolkit offline?",
        a: "Most tools work offline once the page is loaded in your browser since they run entirely client-side. However, the QR Code Generator and some file conversion tools load libraries from a CDN and require an internet connection on first use.",
      },
    ],
  },
  {
    id: "json",
    icon: "{ }",
    title: "JSON Toolkit",
    color: "#F05A41",
    path: "/json-toolkit",
    questions: [
      {
        q: "How do I format and beautify JSON online?",
        a: "Open the JSON Toolkit at minidevtoolkit.com/json-toolkit and go to the Formatter tab. Paste your JSON and it is instantly beautified with proper indentation. You can choose 2, 4, or 8 space indentation and download the formatted JSON as a file.",
      },
      {
        q: "How do I validate JSON online?",
        a: "Open the JSON Toolkit and go to the Validator tab. Paste your JSON and a green banner confirms it is valid, or a red banner shows the exact error and line number if invalid. The validator also shows statistics including data type, item count, key count, maximum nesting depth, file size, and null value count.",
      },
      {
        q: "How do I convert JSON to CSV online for free?",
        a: "Open the JSON Toolkit and go to the CSV tab. Paste your JSON array, preview it as a table, filter which columns to include, choose your separator (comma, semicolon, tab, or pipe), and click Download CSV. The preview table supports row searching, column sorting, and header/quote options.",
      },
      {
        q: "How do I convert JSON to XML online?",
        a: "Open the JSON Toolkit and go to the XML tab. Paste your JSON, optionally set a custom root tag name and item tag name, choose indentation, and download the converted XML file.",
      },
      {
        q: "How do I convert JSON to YAML online?",
        a: "Open the JSON Toolkit and go to the YAML tab. Paste your JSON and it is instantly converted to clean YAML format. Choose indentation and download as a .yaml file.",
      },
      {
        q: "How do I minify JSON online?",
        a: "Open the JSON Toolkit and go to the Minifier tab. Paste your JSON and it is instantly minified — all whitespace and formatting removed. The tool shows original size, minified size, bytes saved, and compression percentage.",
      },
      {
        q: "How do I compare two JSON objects online?",
        a: "Open the JSON Toolkit and go to the Diff tab. Paste one JSON in the left box and another in the right box. The diff checker highlights added, removed, and changed fields with their old and new values shown side by side.",
      },
    ],
  },
  {
    id: "encoding",
    icon: "🔐",
    title: "Encoding Toolkit",
    color: "#22c55e",
    path: "/encoding-toolkit",
    questions: [
      {
        q: "How do I encode or decode Base64 online?",
        a: "Open the Encoding Toolkit at minidevtoolkit.com/encoding-toolkit and select Base64 Encode or Base64 Decode. Paste your text or Base64 string and the output appears instantly. A Swap button lets you flip the output back into input.",
      },
      {
        q: "How do I preview a Base64 image online?",
        a: "Open the Encoding Toolkit and select Base64 Image. Paste a Base64 image string or load an image file from your device. Click Preview and the image renders directly in the browser.",
      },
      {
        q: "How do I URL encode or decode online?",
        a: "Open the Encoding Toolkit and select URL Encode or URL Decode. Paste your URL or percent-encoded string and the result appears instantly. Useful for encoding query parameters or decoding URLs from logs.",
      },
      {
        q: "How do I encode or decode HTML entities online?",
        a: "Open the Encoding Toolkit and select HTML Encode or HTML Decode. HTML Encode converts characters like &, <, >, and \" into their entity equivalents for safe display in HTML. HTML Decode reverses this.",
      },
      {
        q: "How do I decode a JWT token online?",
        a: "Open the Encoding Toolkit and select JWT Decoder. Paste your JWT token and it instantly decodes the header and payload, displays all standard claims (sub, iss, aud, iat, exp, nbf), checks if the token is expired with a clear banner, and shows the raw signature. No secret key is required for decoding.",
      },
      {
        q: "How do I encode or decode Hex online?",
        a: "Open the Encoding Toolkit and select Hex Encode or Hex Decode. Hex Encode converts text to hexadecimal bytes separated by spaces. Hex Decode converts hex bytes back to readable text.",
      },
      {
        q: "How do I escape or unescape Unicode characters online?",
        a: "Open the Encoding Toolkit and select Unicode Escape or Unicode Unescape. Unicode Escape converts non-ASCII characters to \\uXXXX sequences. Unicode Unescape converts \\uXXXX sequences back to readable characters.",
      },
      {
        q: "How do I encode or decode Morse code online?",
        a: "Open the Encoding Toolkit and select Morse Code. Choose Encode (Text to Morse) or Decode (Morse to Text). A reference chart is shown for all letters and numbers. Use / to separate words in Morse code.",
      },
    ],
  },
  {
    id: "generators",
    icon: "⚡",
    title: "Generators",
    color: "#D8A056",
    path: "/generators",
    questions: [
      {
        q: "How do I generate a UUID online?",
        a: "Open the Generators toolkit at minidevtoolkit.com/generators and select UUID Generator. Choose v4 (random), v1 (time-based), or nil format. Set the count (up to 100 at once), toggle UPPERCASE or curly braces format, and click Regenerate. Copy individual UUIDs or download all as a text file.",
      },
      {
        q: "What is the difference between UUID v1 and UUID v4?",
        a: "UUID v4 is randomly generated and is the most commonly used format — ideal for database IDs, session tokens, and API keys. UUID v1 is time-based and includes the current timestamp in the UUID, making it sortable by creation time. Use v4 when you want fully random IDs, v1 when ordering matters.",
      },
      {
        q: "How do I generate a secure password online?",
        a: "Open the Generators toolkit and select Password Generator. Set the length (6 to 64 characters), choose character sets (lowercase, uppercase, numbers, symbols), optionally exclude ambiguous characters like 0, O, l, and 1, and generate up to 50 passwords at once. A strength meter shows how secure each password is.",
      },
      {
        q: "How do I generate Lorem Ipsum placeholder text online?",
        a: "Open the Generators toolkit and select Lorem Ipsum. Choose to generate words, sentences, or paragraphs, set the count, and toggle whether to start with the classic Lorem ipsum dolor sit amet opening. Copy the result with one click.",
      },
      {
        q: "How do I generate a QR code online for free?",
        a: "Open the Generators toolkit and select QR Code. Enter your URL, text, email, or phone number, choose the size (128, 256, or 512px), set error correction level (L, M, Q, or H), pick custom foreground and background colors, and click Generate. Download the QR code as a PNG image.",
      },
      {
        q: "What error correction level should I use for my QR code?",
        a: "L (Low, 7%) is best for clean digital displays. M (Medium, 15%) is the standard for most uses. Q (Quartile, 25%) works when the QR code might be partially obscured. H (High, 30%) is for QR codes on physical materials that may get damaged. Higher correction means more complex patterns and a smaller data capacity.",
      },
    ],
  },
  {
    id: "fake-data",
    icon: "⚗",
    title: "Fake Data Generator",
    color: "#D8A056",
    path: "/fake-data-generator",
    questions: [
      {
        q: "How do I generate fake test data online?",
        a: "Open the Fake Data Generator at minidevtoolkit.com/fake-data-generator. Select from 40+ built-in fields including names, emails, phone numbers, addresses, job titles, companies, UUIDs, credit cards, dates, and more. Set the row count (up to 1,000), preview in table or JSON view, and download as JSON or CSV.",
      },
      {
        q: "What types of fake data can I generate?",
        a: "The Fake Data Generator supports 40+ field types across 7 categories — Identity (first name, last name, full name, username, age, avatar), Contact (email, phone, mobile, website), Address (street, city, state, zip, country, full address), Work (company, job title, department, salary, start date), System (UUID, IP address, status, boolean), Financial (credit card, balance, currency), and Misc (date, score, rating, password, bio, notes).",
      },
      {
        q: "Can I add custom fields to the fake data generator?",
        a: "Yes. Click Add Custom Field in the Fake Data Generator. Name your field and choose from 7 value types: Static Value (same value in every row), Pick from List (randomly picks from your comma-separated values), Random Number (random integer in a range), True/False (random boolean), Incrementing Number (auto-increments per row), UUID (random UUID per row), or Random Date (random date in a range).",
      },
      {
        q: "Can I export fake data as CSV?",
        a: "Yes. After generating data in the Fake Data Generator, click the CSV button to switch to CSV view and click Download CSV. You can also export as JSON. Both formats include all selected fields and custom fields.",
      },
    ],
  },
  {
    id: "hash-tools",
    icon: "#",
    title: "Hash & Security",
    color: "#ef4444",
    path: "/hash-generator",
    questions: [
      {
        q: "How do I generate an MD5 hash online for free?",
        a: "Open the Hash & Security toolkit at minidevtoolkit.com/hash-generator and select the All Hashes tab. Type or paste your text and click Compute Hashes. The MD5 (128-bit) hash is shown instantly. You can also copy it with one click.",
      },
      {
        q: "How do I generate a SHA-256 hash online?",
        a: "Open the Hash & Security toolkit and select the SHA-256 tab. Type or paste your text, optionally toggle uppercase output, and click Hash. The 64-character SHA-256 hash is generated using the Web Crypto API in your browser.",
      },
      {
        q: "How do I generate a SHA-512 hash online?",
        a: "Open the Hash & Security toolkit and select the SHA-512 tab. Type or paste your text and click Hash. The 128-character SHA-512 hash appears. Toggle uppercase for a capital hex output.",
      },
      {
        q: "How do I hash a file online without uploading it?",
        a: "Open the Hash & Security toolkit and select File Hash. Drag and drop any file or click to browse. The tool computes MD5, SHA-256, and SHA-512 hashes for your file entirely in your browser using the Web Crypto API. Your file is never uploaded to any server.",
      },
      {
        q: "Can I reverse or decrypt an MD5 or SHA-256 hash?",
        a: "No. MD5, SHA-256, and SHA-512 are one-way hash functions — they cannot be reversed or decrypted. A hash is a fixed-length fingerprint of the input data. The only way to recover the original input is to guess it and compare hashes, which is why long random passwords are more secure.",
      },
      {
        q: "What is the difference between MD5, SHA-256, and SHA-512?",
        a: "MD5 produces a 128-bit (32 character hex) hash and is fast but considered cryptographically weak — do not use it for security-critical applications. SHA-256 produces a 256-bit (64 character hex) hash and is the industry standard for security. SHA-512 produces a 512-bit (128 character hex) hash and is stronger than SHA-256 but slower. Use SHA-256 or SHA-512 for passwords, file verification, and digital signatures.",
      },
    ],
  },
  {
    id: "file-converter",
    icon: "🔄",
    title: "File Converter",
    color: "#F05A41",
    path: "/file-converter",
    questions: [
      {
        q: "How do I convert PDF to Word (DOCX) online for free?",
        a: "Open the File Converter at minidevtoolkit.com/file-converter and select PDF to Word. Drag and drop your PDF or click to browse, then click Convert. Text is extracted from each page using pdf.js and rebuilt as a formatted DOCX file. The conversion runs entirely in your browser — your file is never uploaded.",
      },
      {
        q: "How do I convert Word (DOCX) to PDF online for free?",
        a: "Open the File Converter and select Word to PDF. Upload your DOCX file, click Convert, and download the PDF. The tool uses mammoth.js to extract content and jsPDF to build the PDF, all in your browser.",
      },
      {
        q: "How do I convert images to PDF online?",
        a: "Open the File Converter and select Images to PDF. Upload multiple JPG, PNG, or WebP images. They are combined into a single PDF in the order uploaded. Click Download PDF when done. The conversion runs entirely in your browser.",
      },
      {
        q: "How do I convert PDF to PNG or images online?",
        a: "Open the File Converter and select PDF to Images. Upload your PDF. Each page is rendered as a separate PNG image using pdf.js. Download individual pages or use Download All to get all pages at once.",
      },
      {
        q: "Are my files uploaded to a server when using the File Converter?",
        a: "No. All file conversions in miniDevToolkit run entirely in your browser. Your PDF, Word documents, and images are processed locally using JavaScript libraries (pdf.js, jsPDF, mammoth.js, docx). Nothing is sent to any server.",
      },
    ],
  },
  {
    id: "time-tools",
    icon: "🕐",
    title: "Time & Date",
    color: "#22c55e",
    path: "/time-date-tools",
    questions: [
      {
        q: "How do I convert a Unix timestamp to a date online?",
        a: "Open the Time & Date tools at minidevtoolkit.com/time-date-tools and go to the Unix Timestamp tab. Paste your Unix timestamp in seconds or milliseconds and see a full breakdown — UTC, ISO 8601, local time, day of week, day of year, and week number. A Live mode shows the current timestamp ticking in real time.",
      },
      {
        q: "How do I convert a date to a Unix timestamp online?",
        a: "Open the Time & Date tools and go to the Unix Timestamp tab. Use the Date to Timestamp input on the right side. Pick a date and time and the tool instantly shows the corresponding Unix timestamp in both seconds and milliseconds.",
      },
      {
        q: "How do I convert time between timezones online?",
        a: "Open the Time & Date tools and go to the Timezone Converter tab. Set your source date and time, choose the source timezone, and add any target timezones from the 35+ available. Each timezone shows the converted time with its UTC offset. Add or remove timezones dynamically.",
      },
      {
        q: "How do I generate a cron expression online?",
        a: "Open the Time & Date tools and go to the Cron Generator tab. Either type a cron expression directly or use the visual builder with individual fields for minute, hour, day of month, month, and day of week. The tool shows a plain English description of when it runs and previews the next 5 execution times. Choose from 18 built-in presets.",
      },
      {
        q: "What cron presets are available in miniDevToolkit?",
        a: "The Cron Generator includes 18 presets: every minute, every 5 minutes, every 15 minutes, every 30 minutes, every hour, every 6 hours, every 12 hours, every day at midnight, every day at noon, every weekday at 9am, every weekend, every Monday, every week on Sunday, 1st of every month, every month, every quarter, every year, and every night at 2am.",
      },
      {
        q: "What is a Unix timestamp?",
        a: "A Unix timestamp is the number of seconds (or milliseconds) that have elapsed since January 1, 1970, 00:00:00 UTC — also known as the Unix epoch. It is widely used in programming, databases, and APIs to represent dates and times in a timezone-independent format.",
      },
    ],
  },
  {
    id: "text-tools",
    icon: "✏",
    title: "Text Utilities",
    color: "#D8A056",
    path: "/text-utilities",
    questions: [
      {
        q: "How do I compare two texts online?",
        a: "Open the Text Utilities at minidevtoolkit.com/text-utilities and go to the Text Diff tab. Paste one text in the left box and another in the right box. Added lines are highlighted green, removed lines red, and changed lines show both old and new versions. A summary shows counts of additions, removals, and changes. Toggle to show or hide unchanged lines.",
      },
      {
        q: "How do I remove duplicate lines from text online?",
        a: "Open the Text Utilities and go to the Remove Duplicates tab. Paste your text (one item per line). Options include case-sensitive matching, trim whitespace, remove empty lines, and sort the output alphabetically. Stats show original line count, unique lines, and duplicates removed.",
      },
      {
        q: "How do I convert text to camelCase online?",
        a: "Open the Text Utilities and go to the Case Converter tab. Paste any text and all 12 case formats appear simultaneously — including camelCase, PascalCase, snake_case, kebab-case, CONSTANT_CASE, dot.case, Title Case, UPPERCASE, lowercase, Sentence case, aLtErNaTiNg, and iNVERT cASE. Click Use on any result to use it as new input.",
      },
      {
        q: "How do I convert camelCase to snake_case online?",
        a: "Open the Text Utilities and go to the Case Converter tab. Paste your camelCase text and the snake_case result appears instantly alongside all other 11 formats. Copy it or click Use to chain further conversions.",
      },
      {
        q: "How do I test a regular expression online?",
        a: "Open the Text Utilities and go to the Regex Tester tab. Type your regular expression, set flags (g for global, i for case-insensitive, m for multiline, s for dot-all), paste your test string, and matches are highlighted in real time. Each match shows its index, length, and value. Use one of 10 built-in presets for email, URL, phone, IPv4, UUID, date, hex color, ZIP code, credit card, or HTML tags.",
      },
      {
        q: "What regex presets are available in miniDevToolkit?",
        a: "The Regex Tester includes 10 presets: Email, URL, US Phone number, IPv4 address, Date (YYYY-MM-DD), Hex color code, UUID, US ZIP code, Credit card number, and HTML tag. Click any preset to load the pattern and flags automatically.",
      },
    ],
  },
  {
    id: "compression",
    icon: "🗜",
    title: "Compression Toolkit",
    color: "#D8A056",
    path: "/compression-toolkit",
    questions: [
      {
        q: "How do I compress an image online for free?",
        a: "Open the Compression Toolkit at minidevtoolkit.com/compression-toolkit and go to the Image tab. Drop one or more JPG, PNG, or WebP images, set the quality slider (1–100%), optionally set a max file size target in KB and a max width in pixels, choose the output format, and click Compress. The max file size is a hard limit — quality is automatically reduced if needed to stay within your target.",
      },
      {
        q: "How does the max file size work with the quality slider?",
        a: "The quality slider sets the starting point for compression. The max file size in KB is a strict hard limit — the output will never exceed it. If the chosen quality produces a file larger than your limit, the compressor automatically reduces quality until the file fits within the target size.",
      },
      {
        q: "How do I compress a PDF online?",
        a: "Open the Compression Toolkit and go to the PDF tab. Upload your PDF, set the image quality slider (lower = smaller file, less sharp text), and click Compress. The tool re-renders each page as a compressed JPEG and rebuilds the PDF. All processing happens in your browser.",
      },
      {
        q: "How do I create a ZIP file online?",
        a: "Open the Compression Toolkit and go to the ZIP tab. Drop any files (all file types accepted), set the compression level (0 = no compression/fastest, 9 = maximum compression/slowest), name your archive, and click Create ZIP. Uses lossless DEFLATE compression — all files are perfectly restored when extracted.",
      },
      {
        q: "How do I optimize an SVG file online?",
        a: "Open the Compression Toolkit and go to the SVG tab. Paste your SVG code or upload an SVG file. The optimizer removes comments, metadata, editor namespaces (Inkscape, Illustrator, Sketch), redundant defaults like opacity=1 and stroke=none, and excessive decimal precision. The visual output is identical — SVG optimization is 100% lossless.",
      },
      {
        q: "How do I minify CSS, HTML, or JSON online?",
        a: "Open the Compression Toolkit and go to the Code tab. Select CSS, HTML, or JSON, paste your code, and the minified output appears instantly. For JSON it uses JSON.parse and JSON.stringify for perfect minification. For CSS and HTML it removes comments and collapses whitespace. All minification is lossless — functionality is 100% preserved.",
      },
      {
        q: "What image formats does the image compressor support?",
        a: "The image compressor supports JPG, PNG, WebP, GIF, and BMP as input. Output can be set to Auto (keeps original format), JPEG, PNG, or WebP. WebP typically produces the smallest file sizes for the same quality level.",
      },
    ],
  },
];

// ─── ACCORDION ITEM ───────────────────────────────────────────────────────────
function AccordionItem({ q, a, t, isOpen, onToggle }) {
  return (
    <div style={{
      borderBottom: `1px solid ${t.border}`,
      transition: "all 0.2s",
    }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", textAlign: "left",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 0", gap: 12,
          background: "none", border: "none", cursor: "pointer",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        <span style={{
          fontSize: 14, fontWeight: isOpen ? 600 : 500,
          color: isOpen ? t.textAccent : t.textPrimary,
          lineHeight: 1.5, flex: 1,
          transition: "color 0.15s",
        }}>
          {q}
        </span>
        <span style={{
          fontSize: 18, color: isOpen ? t.textAccent : t.textMuted,
          transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
          transition: "transform 0.2s, color 0.15s",
          flexShrink: 0, fontWeight: 300, lineHeight: 1,
        }}>
          +
        </span>
      </button>

      {isOpen && (
        <div style={{
          padding: "0 0 16px",
          fontSize: 13,
          color: t.textSecondary,
          lineHeight: 1.75,
          animation: "faq-open 0.2s ease",
        }}>
          {a}
        </div>
      )}
    </div>
  );
}

// ─── MAIN FAQ PAGE ────────────────────────────────────────────────────────────
export default function FaqPage({ t }) {
  const navigate = useNavigate();
  const [openItem, setOpenItem] = useState(null); // "sectionId-index"
  const [activeSection, setActiveSection] = useState("general");
  const [search, setSearch] = useState("");

  const toggle = (key) => setOpenItem(prev => prev === key ? null : key);

  // Filter by search
  const searchLower = search.toLowerCase().trim();
  const filteredSections = searchLower
    ? FAQ_SECTIONS.map(sec => ({
        ...sec,
        questions: sec.questions.filter(
          faq => faq.q.toLowerCase().includes(searchLower) ||
                 faq.a.toLowerCase().includes(searchLower)
        ),
      })).filter(sec => sec.questions.length > 0)
    : FAQ_SECTIONS;

  const totalQ = FAQ_SECTIONS.reduce((s, sec) => s + sec.questions.length, 0);

  return (
    <>
      <Helmet>
        <title>FAQ — Frequently Asked Questions | miniDevToolkit</title>
        <meta name="description"
          content={`Answers to ${totalQ}+ questions about miniDevToolkit's free online developer tools — JSON formatter, Base64 encoder, JWT decoder, UUID generator, hash generator, file converter, regex tester, cron builder, image compressor and more.`} />
        <link rel="canonical" href="https://minidevtoolkit.com/faq" />
        <meta property="og:title"       content="FAQ — miniDevToolkit Free Developer Tools" />
        <meta property="og:description" content={`${totalQ}+ answered questions covering all tools — JSON, encoding, hashing, generators, file conversion, time utilities, text tools and compression.`} />
        <meta property="og:url"         content="https://minidevtoolkit.com/faq" />
        <meta name="twitter:title"       content="FAQ — miniDevToolkit" />
        <meta name="twitter:description" content={`${totalQ}+ questions answered about miniDevToolkit's free browser-based developer tools.`} />

        {/* FAQ Schema — all questions for Google rich results */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_SECTIONS.flatMap(sec =>
            sec.questions.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a },
            }))
          ),
        })}</script>
      </Helmet>

      <style>{`
        @keyframes faq-open {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Hero */}
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about miniDevToolkit's {totalQ}+ free browser-based developer tools.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">{totalQ}+</div><div className="tool-stat-lbl">answers</div></div>
            <div className="tool-stat"><div className="tool-stat-val">{FAQ_SECTIONS.length}</div><div className="tool-stat-lbl">categories</div></div>
          </div>
        </div>
      </div>

      <div className="tool-main">
        {/* Search */}
        <div style={{ marginBottom: "1.5rem", position: "relative" }}>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setActiveSection("general"); }}
            placeholder="Search questions… e.g. 'JWT', 'compress image', 'cron'"
            style={{
              width: "100%",
              background: t.bgInput,
              border: `1px solid ${search ? t.borderActive : t.border}`,
              borderRadius: 10,
              padding: "11px 16px 11px 40px",
              fontSize: 14,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              color: t.textPrimary,
              outline: "none",
              transition: "border-color 0.15s",
            }}
          />
          <span style={{
            position: "absolute", left: 14, top: "50%",
            transform: "translateY(-50%)",
            fontSize: 16, color: t.textMuted,
          }}>⌕</span>
          {search && (
            <button onClick={() => setSearch("")}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 18, lineHeight: 1 }}>
              ×
            </button>
          )}
        </div>

        {/* Search results count */}
        {search && (
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: "1rem", fontFamily: "JetBrains Mono" }}>
            {filteredSections.reduce((s, sec) => s + sec.questions.length, 0)} result{filteredSections.reduce((s, sec) => s + sec.questions.length, 0) !== 1 ? "s" : ""} for "{search}"
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "2rem", alignItems: "start" }}>

          {/* Left nav — category tabs */}
          {!search && (
            <div style={{
              position: "sticky", top: 80,
              display: "flex", flexDirection: "column", gap: 4,
            }}>
              {FAQ_SECTIONS.map(sec => (
                <button key={sec.id}
                  onClick={() => {
                    setActiveSection(sec.id);
                    document.getElementById(`faq-${sec.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 12px", borderRadius: 9, border: "none",
                    background: activeSection === sec.id ? t.bgChipActive : "none",
                    cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12, fontWeight: activeSection === sec.id ? 600 : 400,
                    color: activeSection === sec.id ? t.textAccent : t.textSecondary,
                    textAlign: "left", transition: "all 0.15s",
                    boxShadow: activeSection === sec.id ? `inset 3px 0 0 ${t.borderActive}` : "none",
                  }}
                >
                  <span style={{ fontSize: 13, fontFamily: "JetBrains Mono", color: activeSection === sec.id ? sec.color : t.textMuted }}>
                    {sec.icon}
                  </span>
                  {sec.title}
                  <span style={{ marginLeft: "auto", fontSize: 10, color: t.textMuted, fontFamily: "JetBrains Mono" }}>
                    {sec.questions.length}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Right — FAQ content */}
          <div style={{ minWidth: 0 }}>
            {filteredSections.map(sec => (
              <div key={sec.id} id={`faq-${sec.id}`}
                style={{ marginBottom: "2.5rem" }}>

                {/* Section header */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  marginBottom: "1rem",
                  paddingBottom: 10,
                  borderBottom: `2px solid ${sec.color}33`,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: `${sec.color}18`,
                    border: `1px solid ${sec.color}33`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontFamily: "JetBrains Mono",
                    color: sec.color, fontWeight: 700,
                  }}>
                    {sec.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary }}>{sec.title}</div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>{sec.questions.length} questions</div>
                  </div>
                  {sec.path && (
                    <button
                      onClick={() => navigate(sec.path)}
                      className="btn-sm"
                      style={{ marginLeft: "auto" }}
                    >
                      Open tool →
                    </button>
                  )}
                </div>

                {/* Questions */}
                {sec.questions.map((faq, i) => {
                  const key = `${sec.id}-${i}`;
                  return (
                    <AccordionItem key={key}
                      q={faq.q} a={faq.a} t={t}
                      isOpen={openItem === key}
                      onToggle={() => toggle(key)}
                    />
                  );
                })}
              </div>
            ))}

            {/* No results */}
            {search && filteredSections.length === 0 && (
              <div style={{ textAlign: "center", padding: "3rem 1rem", color: t.textMuted }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>◎</div>
                <div style={{ fontSize: 14, marginBottom: 8 }}>No results for "{search}"</div>
                <button className="btn-sm" onClick={() => setSearch("")}>Clear search</button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{
          marginTop: "3rem", padding: "1.5rem",
          background: t.bgChipActive,
          border: `1px solid ${t.border}`,
          borderRadius: 14, textAlign: "center",
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 6 }}>
            Still have questions?
          </div>
          <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 16 }}>
            Browse all tools or reach out via the portfolio contact page.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn-primary" onClick={() => navigate("/")}
              style={{ padding: "9px 22px", fontSize: 13 }}>
              Browse all tools
            </button>
            <a href="https://aqibjaved.vercel.app/" target="_blank" rel="noopener noreferrer"
              className="btn-secondary"
              style={{ padding: "9px 22px", fontSize: 13 }}>
              Contact
            </a>
          </div>
        </div>
      </div>
    </>
  );
}